import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import styled from 'astroturf/react';

import routes from 'js/core_pre_webpack/routes';
import { Body } from 'js/design_system/Body';
import { BodyLink } from 'js/design_system/link/BodyLink';
import { Icon } from 'js/design_system/Icon';
import CloseIcon from 'js/design_system/icons/close.svg';
import {
  DeprecatedGrid,
  GridColumn,
} from 'js/design_system/grid/DeprecatedGrid';
import getResponsivenessTraits, {
  PageName,
} from 'js/header/responsivenessTraits';
import SearchBar from 'js/header/SearchBar/SearchBar';
import { currentUser } from 'js/utils/user';
import { get } from 'js/utils/api_utils';
import useScreenSize from 'js/utils/hooks/useScreenSize';
import useScrollLock from 'js/utils/hooks/useScrollLock';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';

import AcademiaIcon from './AcademiaIcon';
import AccountMenu from './AccountMenu';
import AccountMenuButton from './AccountMenuButton';
import SmallAccountMenuButton from './SmallAccountMenuButton';
import ToolsMenu from './ToolsMenu';
import ToolsSidebar from './ToolsSidebar';
import { AgsAiTwinMarketingButton } from './AgsAiTwinMarketingButton';
import {
  grantsMarketingIconButton,
  GrantsMarketingButton,
} from './GrantsMarketingButton';
import ImpactNavButton from './ImpactNavButton';
import PodcastLibraryButton from './PodcastLibraryButton';
import PremiumUpsellButton from './PremiumUpsellButton';
import UploadButton from './UploadButton';
import {
  IconAnchor,
  IconButton,
  IconContainer,
  IconLabel,
  NotificationDot,
} from './buttonStyles';
import { setDocumentTitleCount } from './documentTitleCount';
import { useFocusedNavDynamicProps } from './useFocusedNavDynamicProps';
import _NavAndTools from "FocusedNav-NavAndTools.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=NavAndTools";
import _NavContainer from "FocusedNav-NavContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=NavContainer";
import _MobileNavContainer from "FocusedNav-MobileNavContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=MobileNavContainer";
import _MobileSearchContainer from "FocusedNav-MobileSearchContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=MobileSearchContainer";
import _WidthContainer from "FocusedNav-WidthContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=WidthContainer";
import _SearchBarContainer from "FocusedNav-SearchBarContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=SearchBarContainer";
import _IconAnchorsContainer from "FocusedNav-IconAnchorsContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=IconAnchorsContainer";
import _FullViewMenu from "FocusedNav-FullViewMenu.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=FullViewMenu";
import _CloseMenuButton from "FocusedNav-CloseMenuButton.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=CloseMenuButton";
import _BottomMenu from "FocusedNav-BottomMenu.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=BottomMenu";
import _RestrictedUserBannerContainer from "FocusedNav-RestrictedUserBannerContainer.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=RestrictedUserBannerContainer";
import _RestrictedUserBannerLink from "FocusedNav-RestrictedUserBannerLink.module.scss!=!astroturf/inline-loader?style=1!/var/lib/jenkins-code/academia-app/app/assets/javascripts/header/FocusedNav/FocusedNav.tsx?styleId=RestrictedUserBannerLink";


// The nav switches to the mobile style below this value (equal to $ds-tablet).
// If changing this value must also update '.focused-nav-container' styles.
export const HAMBURGER_MENU_SIZE = 768; // px
const NAV_HEIGHT_DESKTOP = 80;
export const NAV_HEIGHT_MOBILE = 56;

const NavAndTools = /*#__PURE__*/styled("div", null, {
  displayName: "NavAndTools",
  styles: _NavAndTools
});

const NavContainer = /*#__PURE__*/styled("div", null, {
  displayName: "NavContainer",
  styles: _NavContainer
});

const MobileNavContainer = /*#__PURE__*/styled(NavContainer, null, {
  displayName: "MobileNavContainer",
  styles: _MobileNavContainer
});

const MobileSearchContainer = /*#__PURE__*/styled("div", null, {
  displayName: "MobileSearchContainer",
  styles: _MobileSearchContainer
});

const WidthContainer = /*#__PURE__*/styled("div", null, {
  displayName: "WidthContainer",
  styles: _WidthContainer
});

const SearchBarContainer = /*#__PURE__*/styled("div", null, {
  displayName: "SearchBarContainer",
  styles: _SearchBarContainer
});

const IconAnchorsContainer = /*#__PURE__*/styled("div", null, {
  displayName: "IconAnchorsContainer",
  styles: _IconAnchorsContainer
});

const FullViewMenu = /*#__PURE__*/styled("div", null, {
  displayName: "FullViewMenu",
  styles: _FullViewMenu
});

const CloseMenuButton = /*#__PURE__*/styled("button", null, {
  displayName: "CloseMenuButton",
  styles: _CloseMenuButton
});

const BottomMenu = /*#__PURE__*/styled("div", null, {
  displayName: "BottomMenu",
  styles: _BottomMenu
});

const RestrictedUserBannerContainer = /*#__PURE__*/styled(Body, null, {
  displayName: "RestrictedUserBannerContainer",
  styles: _RestrictedUserBannerContainer
});

const RestrictedUserBannerLink = /*#__PURE__*/styled(BodyLink, null, {
  displayName: "RestrictedUserBannerLink",
  styles: _RestrictedUserBannerLink
});

type RestrictedUserBannerProps = {
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
};

const RestrictedUserBanner = ({
  restrictedUser,
  confirmAccountEmail,
}: RestrictedUserBannerProps) => {
  if (!restrictedUser) {
    return null;
  }

  switch (restrictedUser) {
    case 'unconfirmed':
      return (
        <RestrictedUserBannerContainer>
          Please confirm your account. We&apos;ve sent a confirmation link to{' '}
          {confirmAccountEmail}.
          <RestrictedUserBannerLink underline href="/confirm/send_new_email">
            Resend
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/confirm/sent#email">
            Change Email
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious by an automated system. If this
          was an error, please accept our apologies.
          <RestrictedUserBannerLink underline href="/user_unflag_requests/new">
            Request Unflagging
          </RestrictedUserBannerLink>
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    case 'flagged_pending':
      return (
        <RestrictedUserBannerContainer>
          Your account was flagged as suspicious, and your reconsideration
          request is awaiting manual review. We&apos;re sorry for any
          inconvenience.
          <RestrictedUserBannerLink underline href="/settings">
            Account Settings
          </RestrictedUserBannerLink>
        </RestrictedUserBannerContainer>
      );
    default: {
      const exhaustiveCheck: never = restrictedUser;
      throw new Error(`Unhandled restricted user type: ${exhaustiveCheck}`);
    }
  }
};

type ResponsiveIcon = {
  icon: ReactNode;
  // Lower priority wins on smaller screens.
  priority: number;
};

type ContainerProps = {
  useGrid: boolean;
  maxWidth: number | undefined;
  children: React.ReactNode;
};

const Container = ({ useGrid, maxWidth, children }: ContainerProps) =>
  useGrid ? (
    <DeprecatedGrid>
      <GridColumn sm={2} md={8} lg={12} xl={12}>
        <WidthContainer>{children}</WidthContainer>
      </GridColumn>
    </DeprecatedGrid>
  ) : (
    <WidthContainer style={{ maxWidth }}>{children}</WidthContainer>
  );

type Menu = 'account' | 'tools' | 'search';

type RestrictedUserType = 'unconfirmed' | 'flagged' | 'flagged_pending';

type FocusedNavProps = {
  inDevStyle?: boolean;
  pageName: PageName;
  useGrid?: boolean;
  initialQuery?: string;
  isPremium?: boolean;
  hidePremiumUpsellButton?: boolean;
  restrictedUser?: RestrictedUserType;
  confirmAccountEmail?: string;
};

const FocusedNav = ({
  inDevStyle,
  pageName,
  useGrid = false,
  initialQuery,
  isPremium = false,
  hidePremiumUpsellButton,
  restrictedUser,
  confirmAccountEmail,
}: FocusedNavProps) => {
  // Allow conditional rendering of components that aren't SSR-safe
  const [isClient, setIsClient] = useState(false);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const dynamicProps = useFocusedNavDynamicProps();
  const notifications = dynamicProps?.notifications;
  const premiumUpsells = dynamicProps?.premium_upsells;
  const featureFlags = dynamicProps?.feature_flags;

  // Feature flags loaded asynchronously
  const showGrantsButtonOnNav = featureFlags?.showGrantsButtonOnNav ?? false;
  const showAiAssistantNav = featureFlags?.showAiAssistantNav ?? false;
  const showImpactHubNav = featureFlags?.showImpactHubNav ?? false;
  const showPodcastLibraryNav = featureFlags?.showPodcastLibraryNav ?? false;

  const [openMenu, setOpenMenu] = useState<null | Menu>(null);
  const [searchBarFocusForced, setSearchBarFocusForced] = useState(false);
  const { screenWidth } = useScreenSize();
  const { maxWidth } = getResponsivenessTraits(pageName, screenWidth);
  const toolsButtonRef = useRef<HTMLButtonElement>(null);
  const [notificationCount, setNotificationCount] = useState(0);
  const [mentionsCount, setMentionsCount] = useState(0);

  const isMobileNav = screenWidth < HAMBURGER_MENU_SIZE;

  // Which icon to show at top of mobile nav
  const mobileTopIcon = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (showAiAssistantNav) {
      return 'ai_assistant_top';
    }
    if (isPremium) {
      return 'tools_top';
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library_top';
    }
    return 'upsell_top';
  }, [isPremium, restrictedUser, showAiAssistantNav, showPodcastLibraryNav]);

  // A button to show next to the menu on desktop
  const desktopHighlightedButton = useMemo(() => {
    if (restrictedUser) {
      return null;
    }
    if (showAiAssistantNav) {
      return 'ags_ai_twin';
    }
    if (showPodcastLibraryNav) {
      return 'podcast_library';
    }
    if (!isPremium && !hidePremiumUpsellButton) {
      return 'premium_upsell';
    }
    if (isPremium && showGrantsButtonOnNav) {
      return 'grants';
    }
    return null;
  }, [
    hidePremiumUpsellButton,
    isPremium,
    restrictedUser,
    showAiAssistantNav,
    showPodcastLibraryNav,
    showGrantsButtonOnNav,
  ]);

  useEffect(() => {
    const url = routes.unread_count_api_v0_notification_messages_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setNotificationCount(count));
  }, []);

  useEffect(() => {
    const url = routes.unread_count_api_v0_mentions_path({
      subdomain_param: 'api',
    });
    get(url).then(({ count }) => setMentionsCount(count));
  }, []);

  const totalCount =
    notificationCount +
    mentionsCount +
    (notifications?.messagesCount ?? 0) +
    (notifications?.hasUnseenReaders ? 1 : 0);

  // SSR-safe: Update document title in useEffect to avoid hydration mismatch
  useEffect(() => {
    setDocumentTitleCount(totalCount);
  }, [totalCount]);

  const startSearch = () => {
    setSearchBarFocusForced(true);
    setTimeout(() => setSearchBarFocusForced(false), 100);
  };

  const toggleMenu = useCallback(
    (menu: Menu): void => {
      if (openMenu === menu) {
        setOpenMenu(null);
      } else {
        setOpenMenu(menu);
      }
    },
    [openMenu],
  );

  const homeButton = useMemo(
    () => (
      <IconAnchor
        key="home"
        href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
      >
        <IconContainer>
          <Icon name="home" size={28} />
        </IconContainer>
        <IconLabel>Home</IconLabel>
      </IconAnchor>
    ),
    [],
  );

  const impactButton = useMemo(() => <ImpactNavButton />, []);

  const mentionsButton = useMemo(
    () => (
      <IconAnchor
        key="mentions"
        href={
          isPremium
            ? routes.mentions_url({ from_navbar: true, trigger: 'nav' })
            : routes.upgrade_url({
                feature: 'searchm',
                from_navbar: true,
                trigger: 'nav',
              })
        }
      >
        <IconContainer>
          <Icon name="format_quote" size={28} />
        </IconContainer>
        <IconLabel>
          Mentions
          {mentionsCount > 0 && <NotificationDot />}
        </IconLabel>
      </IconAnchor>
    ),
    [mentionsCount, isPremium],
  );

  const analyticsButton = useMemo(
    () =>
      isClient ? (
        <IconAnchor
          key="analytics"
          href={routes.analytics_dashboard_url({
            page_name: currentUser?.page_name,
            subdomain: currentUser?.domain_name,
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bar_chart" size={28} />
          </IconContainer>
          <IconLabel>Analytics</IconLabel>
        </IconAnchor>
      ) : null,
    [isClient],
  );

  const uploadButton = useMemo(
    () => (
      <UploadButton
        key="upload"
        hasUnseenClaimablePapers={
          notifications?.hasUnseenClaimablePapers ?? false
        }
        hasUnseenUploadableStubs={
          notifications?.hasUnseenUploadableStubs ?? false
        }
      />
    ),
    [
      notifications?.hasUnseenClaimablePapers,
      notifications?.hasUnseenUploadableStubs,
    ],
  );

  const toolsButton = useMemo(
    () => (
      <IconButton
        ref={toolsButtonRef}
        key="tools"
        onClick={() => toggleMenu('tools')}
        aria-pressed={openMenu === 'tools'}
      >
        <IconContainer>
          <Icon name="apps" size={28} />
        </IconContainer>
        <IconLabel>Tools</IconLabel>
      </IconButton>
    ),
    [openMenu, toggleMenu],
  );

  const grantsMultiplierButton = useMemo(() => grantsMarketingIconButton, []);

  const libraryButton = useMemo(
    () => (
      <ImpressionWatcher source="ags_nav.library">
        <IconAnchor
          key="library"
          href={routes.bookmarks_url({
            from_navbar: true,
            trigger: 'nav',
          })}
        >
          <IconContainer>
            <Icon name="bookmark" size={24} />
          </IconContainer>
          <IconLabel>Your library</IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    ),
    [],
  );

  const aiAssistantHomeButton = useMemo(
    () => (
      <ImpressionWatcher source="ags_nav.newsfeed">
        <IconAnchor
          key="ai-assistant-home"
          href={routes.home_url({ from_navbar: true, trigger: 'nav' })}
        >
          <IconContainer>
            <Icon name="newsmode" size={24} />
          </IconContainer>
          <IconLabel>Newsfeed</IconLabel>
        </IconAnchor>
      </ImpressionWatcher>
    ),
    [],
  );

  const icons = useMemo(() => {
    const showGrantMultiplier =
      !isMobileNav && !isPremium && showGrantsButtonOnNav;
    const showImpactHub = showImpactHubNav && !showGrantsButtonOnNav;

    if (showAiAssistantNav) {
      return [aiAssistantHomeButton, libraryButton, toolsButton];
    }

    const unfilteredIcons: (ResponsiveIcon | false)[] = [
      { icon: homeButton, priority: 5 },
      showImpactHub && { icon: impactButton, priority: 2 },
      { icon: mentionsButton, priority: 0 },
      showGrantMultiplier && { icon: grantsMultiplierButton, priority: 4 },
      { icon: analyticsButton, priority: 1 },
      { icon: uploadButton, priority: 3 },
      { icon: toolsButton, priority: -Infinity },
    ];
    const icons: ResponsiveIcon[] = unfilteredIcons.filter(
      // Type guard is needed here to convince typescript that there are no false values
      (icon): icon is ResponsiveIcon => !!icon,
    );

    if (isPremium || hidePremiumUpsellButton) {
      return icons.map((icon) => icon.icon);
    }

    // Hide another icon every 50 pixels past the max width.
    const iconsWithPriority = icons.filter(
      (icon) => icon.priority !== undefined,
    ).length;
    const baseMaxWidth = 950; // Arbitrary pixels that respond best
    const maxWidth =
      baseMaxWidth + (premiumUpsells?.reactivatePremiumUpsellType ? 273 : 127); // Premium button sizes
    const maxLevel =
      iconsWithPriority -
      Math.floor((maxWidth - Math.min(screenWidth, maxWidth)) / 50);
    return icons
      .filter((icon) => icon.priority <= maxLevel)
      .map((icon) => icon.icon);
  }, [
    premiumUpsells?.reactivatePremiumUpsellType,
    screenWidth,
    analyticsButton,
    homeButton,
    impactButton,
    mentionsButton,
    uploadButton,
    toolsButton,
    hidePremiumUpsellButton,
    isMobileNav,
    grantsMultiplierButton,
    showGrantsButtonOnNav,
    libraryButton,
    aiAssistantHomeButton,
    showAiAssistantNav,
    showImpactHubNav,
    isPremium,
  ]);

  useScrollLock(isMobileNav && openMenu !== null);

  const closeMenuButton = (
    <CloseMenuButton onClick={() => setOpenMenu(null)}>
      <CloseIcon color="#222222" />
    </CloseMenuButton>
  );

  if (isMobileNav) {
    return (
      <>
        <NavAndTools isMobileNav={isMobileNav}>
          <MobileNavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
            <Container maxWidth={maxWidth} useGrid={useGrid}>
              <SmallAccountMenuButton
                count={totalCount}
                menuOpen={openMenu === 'account'}
                setMenuOpen={(open) => setOpenMenu(open ? 'account' : null)}
              />
              <MobileSearchContainer>
                <ImpressionWatcher source="focused_nav_search_bar">
                  <SearchBar
                    initialQuery={initialQuery}
                    focusForced={searchBarFocusForced}
                    isPremium={isPremium}
                  />
                </ImpressionWatcher>
              </MobileSearchContainer>
              {mobileTopIcon === 'tools_top' && (
                <div onClick={() => toggleMenu('tools')}>{toolsButton}</div>
              )}
              {mobileTopIcon === 'upsell_top' && (
                <PremiumUpsellButton
                  premiumUpsells={premiumUpsells}
                  isMobileNav={isMobileNav}
                />
              )}
              {mobileTopIcon === 'ai_assistant_top' && (
                <AgsAiTwinMarketingButton />
              )}
              {mobileTopIcon === 'podcast_library_top' && (
                <PodcastLibraryButton />
              )}
            </Container>
          </MobileNavContainer>
          {openMenu === 'tools' && (
            <FullViewMenu>
              {closeMenuButton}
              <ToolsMenu
                startSearch={startSearch}
                hasUnseenClaimablePapers={
                  notifications?.hasUnseenClaimablePapers ?? false
                }
                hasUnseenUploadableStubs={
                  notifications?.hasUnseenUploadableStubs ?? false
                }
              />
            </FullViewMenu>
          )}
          {openMenu === 'account' && (
            <FullViewMenu>
              {closeMenuButton}
              <AccountMenu
                restrictedUser={Boolean(restrictedUser)}
                halfOffCoupon={premiumUpsells?.halfOffCoupon}
                reactivate={premiumUpsells?.reactivatePremiumUpsellType}
                mentionsCount={mentionsCount}
                messagesCount={notifications?.messagesCount ?? 0}
                hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
                notificationCount={notificationCount}
              />
            </FullViewMenu>
          )}
          {!restrictedUser && (
            <BottomMenu>
              {showAiAssistantNav ? (
                <>
                  {aiAssistantHomeButton}
                  {libraryButton}
                  {toolsButton}
                </>
              ) : (
                <>
                  {homeButton}
                  {showImpactHubNav && !showGrantsButtonOnNav && impactButton}
                  {mentionsButton}
                  {analyticsButton}
                  {mobileTopIcon !== 'tools_top' ? toolsButton : uploadButton}
                </>
              )}
            </BottomMenu>
          )}
        </NavAndTools>
        <RestrictedUserBanner
          restrictedUser={restrictedUser}
          confirmAccountEmail={confirmAccountEmail}
        />
      </>
    );
  }

  return (
    <>
      <NavAndTools isMobileNav={isMobileNav}>
        <NavContainer inDevStyle={inDevStyle} useGrid={useGrid}>
          <Container maxWidth={maxWidth} useGrid={useGrid}>
            <AcademiaIcon />
            <SearchBarContainer>
              <ImpressionWatcher source="focused_nav_search_bar">
                <SearchBar
                  initialQuery={initialQuery}
                  focusForced={searchBarFocusForced}
                  isPremium={isPremium}
                />
              </ImpressionWatcher>
            </SearchBarContainer>
            {!restrictedUser && (
              <IconAnchorsContainer>{icons}</IconAnchorsContainer>
            )}
            {desktopHighlightedButton === 'premium_upsell' && (
              <PremiumUpsellButton
                premiumUpsells={premiumUpsells}
                isMobileNav={isMobileNav}
              />
            )}
            {desktopHighlightedButton === 'grants' && <GrantsMarketingButton />}
            {desktopHighlightedButton === 'ags_ai_twin' && (
              <AgsAiTwinMarketingButton />
            )}
            {desktopHighlightedButton === 'podcast_library' && (
              <PodcastLibraryButton />
            )}
            <AccountMenuButton
              restrictedUser={Boolean(restrictedUser)}
              halfOffCoupon={premiumUpsells?.halfOffCoupon}
              reactivate={premiumUpsells?.reactivatePremiumUpsellType}
              totalCount={totalCount}
              notificationCount={notificationCount}
              mentionsCount={mentionsCount}
              messagesCount={notifications?.messagesCount ?? 0}
              hasUnseenReaders={notifications?.hasUnseenReaders ?? false}
              open={openMenu === 'account'}
              setOpen={(open) => setOpenMenu(open ? 'account' : null)}
            />
          </Container>
        </NavContainer>

        {openMenu === 'tools' && (
          <ToolsSidebar
            buttonRef={toolsButtonRef}
            close={() => setOpenMenu(null)}
            startSearch={startSearch}
            navHeight={NAV_HEIGHT_DESKTOP}
            hasUnseenClaimablePapers={
              notifications?.hasUnseenClaimablePapers ?? false
            }
            hasUnseenUploadableStubs={
              notifications?.hasUnseenUploadableStubs ?? false
            }
          />
        )}
      </NavAndTools>
      <RestrictedUserBanner
        restrictedUser={restrictedUser}
        confirmAccountEmail={confirmAccountEmail}
      />
    </>
  );
};

export default FocusedNav;
